import moment from 'moment';

export type TimeUnits = moment.DurationInputArg2 & moment.unitOfTime.StartOf;

export class DateTimeHelper {
  private static dateRegex = /^\d\d.\d\d.\d\d\d\d$/;
  private static timeRegex = /^\d\d\:\d\d(\:\d\d)?(\.\d\d\d)?$/;

  static dateFormat: string = 'DD.MM.YYYY';
  static timeFormat: string = 'HH:mm';
  static timeWithSecondsFormat: string = 'HH:mm:ss';
  static timeWithMillisecondsFormat: string = 'HH:mm:ss.SSS';
  static monthYearFormat: string = 'MMMM YYYY';

  static now(): Date {
    return new Date();
  }

  static currentTimeMillis(): number {
    return new Date().getTime();
  }

  static copyDate(date: Date | string | number): Date {
    return new Date(date);
  }

  static differenceMillis(datetimeFirst: Date | string | number, datetimeSecond: Date | string | number): number {
    return new Date(datetimeFirst).getTime() - new Date(datetimeSecond).getTime();
  }

  static isValid(date: moment.MomentInput): boolean {
    return moment(date).isValid();
  }

  static setLocale(
    uiLanguage: 'de' | 'fr' | 'it',
    invalidDateText: string
  ): void {
    moment.locale(uiLanguage);
    moment.updateLocale(uiLanguage, {
      invalidDate: invalidDateText
    });
  }

  static compareAsc(a: moment.MomentInput, b: moment.MomentInput): number {
    const dateA = this.toMoment(a);
    const dateB = this.toMoment(b);

    return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
  }

  static getNowObjectAtStartOf(unit: TimeUnits): Date {
    return moment()
      .startOf(unit)
      .toDate();
  }
  static getDateObjectAtStartOf(
    date: moment.MomentInput,
    unit: TimeUnits
  ): Date {
    return this.toMoment(date)
      .startOf(unit)
      .toDate();
  }

  static durationDecimal(
    startDate: moment.MomentInput,
    endDate: moment.MomentInput,
    unit: TimeUnits
  ): number {
    return this.toMoment(endDate).diff(this.toMoment(startDate), unit, true);
  }

  static getMinutesOfDay(dateTime: moment.MomentInput): number {
    return moment.duration(DateTimeHelper.toTimestring(dateTime)).asMinutes();
  }

  static toTimestring(
    dateTime: moment.MomentInput,
    format: string = this.timeFormat
  ) {
    if (dateTime === null || dateTime === '') {
      return '';
    }
    return this.toMoment(dateTime).format(format);
  }

  static toMonthAndYear(
    date: moment.MomentInput
  ) {
    return this.toMoment(date).format(this.monthYearFormat);
  }

  static addDurationToDate(
    date: moment.MomentInput,
    duration: number,
    timeUnit: TimeUnits = 'days'
  ): Date {
    return this.toMoment(date)
      .add(duration, timeUnit)
      .toDate();
  }

  static getDatesBetween(startDate: moment.MomentInput, endDate: moment.MomentInput): Date[] {
    const start = this.toMoment(startDate);
    const end = this.toMoment(endDate);
    const dates: Date[] = [];

    let currentDate = start.clone();
    while (currentDate <= end) {
        dates.push(currentDate.toDate());
        currentDate = currentDate.clone().add(1, 'days');
    }

    return dates;
}

static getDatesInRange(startDate: moment.MomentInput, endDate: moment.MomentInput): Date[] {
    return this.getDatesBetween(startDate, endDate);
}

static getDaysDifference(date1: moment.MomentInput, date2: moment.MomentInput): number {
    return Math.abs(this.toMoment(date2).diff(this.toMoment(date1), 'days'));
}

  // TODO:

  // static roundDate(date: moment.MomentInput, timeRange: number){
  //   const momentDate = this.toMoment(date);

  //   momentDate.minute()

  //   momentDate.add(momentDate.minute() > 30 && 1, 'minutes').minutes(moment().minute() <= timeRange ? timeRange : 0)
  // }

  static duration(startDate: Date, endDate: Date, unit: TimeUnits): number {
    return this.toMoment(endDate).diff(this.toMoment(startDate), unit);
  }

  static removeSecondsFromTime(time: string): string {
    return moment(time, 'HH:mm:ss').format('HH:mm');
  }

  static getDaysInMonth(date: moment.MomentInput): number {
    return this.toMoment(date).daysInMonth();
  }

  static isSame(dateA: moment.MomentInput, dateB: moment.MomentInput): boolean {
    return this.toMoment(dateA).isSame(this.toMoment(dateB));
  }

  static isSameDay(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isSame(this.toMoment(dateB), 'day');
  }

  static isBefore(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isBefore(this.toMoment(dateB));
  }

  static isAfter(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isAfter(this.toMoment(dateB));
  }

  static isSameOrBefore(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput,
    unit?: moment.unitOfTime.StartOf
  ): boolean {
    if (!unit) {
      return this.toMoment(dateA).isSameOrBefore(this.toMoment(dateB));
    } else {
      return this.toMoment(dateA).isSameOrBefore(this.toMoment(dateB), unit);
    }
  }

  static isSameOrAfter(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput,
    unit?: moment.unitOfTime.StartOf
  ): boolean {
    if (!unit) {
      return this.toMoment(dateA).isSameOrAfter(this.toMoment(dateB));
    } else {
      return this.toMoment(dateA).isSameOrAfter(this.toMoment(dateB), unit);
    }
  }

  static isSameOrAfterMinute(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isSameOrAfter(this.toMoment(dateB), 'minute');
  }

  static isAfterDay(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isAfter(this.toMoment(dateB), 'day');
  }

  static isSameMonth(
    dateA: moment.MomentInput,
    dateB: moment.MomentInput
  ): boolean {
    return this.toMoment(dateA).isSame(this.toMoment(dateB), 'month');
  }

  /**
   * Check if a date is between two other dates
   * @param date
   * @param fromDate
   * @param toDate
   * @param unit If you want to limit the granularity to a unit
   * @param inclusivity A [ indicates inclusion of a value. A ( indicates exclusion. If the inclusivity parameter is used, both indicators must be passed.
   * @returns
   */
  static isBetween(
    date: moment.MomentInput,
    fromDate: moment.MomentInput,
    toDate: moment.MomentInput,
    unit: TimeUnits,
    inclusivity: '[]' | '()' | '[)' | '(]' = '[]'
  ): boolean {
    return this.toMoment(date).isBetween(
      this.toMoment(fromDate),
      this.toMoment(toDate),
      unit,
      inclusivity
    );
  }

  static setTimeOfDate(date: moment.MomentInput, time: string): Date {
    if (!this.timeRegex.test(time)) {
      time = this.toTimeString(time, this.timeWithMillisecondsFormat);
    }
    const dateObject = this.toDateObject(date);
    const mainParts = time.split('.');
    const timeParts = mainParts[0].split(':');

    dateObject.setHours(+timeParts[0]);
    dateObject.setMinutes(+timeParts[1]);

    if (timeParts.length > 2) {
      dateObject.setSeconds(+timeParts[2]);
    } else {
      dateObject.setSeconds(0);
    }

    if (mainParts.length === 2) {
      dateObject.setMilliseconds(+mainParts[1]);
    } else {
      dateObject.setMilliseconds(0);
    }

    return dateObject;
  }

  static setTimePart(date: Date, timePartValue: number, timeType: 'second' | 'minute' | 'hour') {
    switch (timeType) {
      case 'second':
        return date.setSeconds(timePartValue);
      case 'minute':
        return date.setMinutes(timePartValue);

      default:
        return date.setHours(timePartValue);
    }
  }

  static convertDate(date: string | Date): Date {
    return moment(date).toDate();
  }

  static toDateString(
    dateTime: moment.MomentInput,
    format: string = this.dateFormat
  ): string {
    if (dateTime === null || dateTime === '') {
      return '';
    }
    return this.toMoment(dateTime).format(format);
  }

  static getNowString(): string {
    return moment().format();
  }

  static getNowObject(): Date {
    return moment().toDate();
  }

  static getTomorrowObject(): Date {
    return moment()
      .add(1, 'days')
      .toDate();
  }

  static toTimeString(
    dateTime: moment.MomentInput,
    format: string = this.timeFormat
  ): string {
    if (dateTime === null || dateTime === '') {
      return '';
    }
    return this.toMoment(dateTime).format(format);
  }

  static toDateObject(dateTime: moment.MomentInput): Date {
    return this.toMoment(dateTime).toDate();
  }

  /**
   * Format a Date using the ISO Standard format and keeping the local timezone.
   * No millisec are returned.
   */
  static toIsoFormat(dateToFormat: moment.MomentInput): string {
    // TODO: restore as check
    // if (dateToFormat === null || dateToFormat === '' || dateToFormat === undefined) {
    //   return null;
    // }

    const date = this.toMoment(dateToFormat).toDate();

    var dtOffset = new Date(
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
    );

    return dtOffset
      .toISOString()
      .split('.')[0]
      .replace('Z', '');
  }

  static setDateToTime(datePart: Date, time: Date) {
    let date = moment(datePart);
    let selectedDateTime = moment(time);

    selectedDateTime.date(date.date());
    selectedDateTime.month(date.month());
    selectedDateTime.year(date.year());
    return selectedDateTime.toDate();
  }

  /**
   * Adds a ISO duration to a given date.
   * @param duration The ISO Duration. Eg. 'P12D'
   * @param date The target date of type `Date`
   * @returns The date passed as parameter shifted of the given duration.
   */
  static addIsoDuration(duration: string, date: Date) {
    const d = moment.duration(duration);
    return moment(date).add(d).toDate();
  }

  static convertIsoDuration(duration: string, timeType: 'sec' | 'min' | 'hour' | 'day' = 'sec'): number {
    if (!duration) {
      return 0;
    }

    const d = moment.duration(duration);
    switch (timeType) {
      case 'sec':
        return d.asSeconds();
      case 'min':
        return d.asMinutes();
      case 'hour':
        return d.asHours();
      case 'day':
        return d.asDays();

      default:
        return d.asSeconds();
    }
  }

  static truncate(
    dateTime: moment.MomentInput,
    type: 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second' = 'day'
  ): Date {
    return this.toMoment(dateTime).startOf(type).toDate();
  }

  private static toMoment(date: moment.MomentInput): moment.Moment {
    return moment.parseZone(date).local(true);
  }
}
